import ErrorIcon from 'assets/svg/error/error-sm.svg'
import WalletIcon from 'assets/svg/profile/wallet.svg'
import Flex from 'common/components/Flex'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import CurrencyFormatter from 'lib/currency-formatter'
import {
  useFeatureFlagLoaded,
  useFeatureFlags,
} from 'lib/feature-flags'
import { useCallback, useEffect, useMemo } from 'react'
import { IData } from 'services/Wallet/models/WalletModel/interface'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const MenuLink = styled.a`
  ${theme.typography.rsubtitle}
  display: flex;
  padding: 8px;
  margin: 2px;
  align-items: center;
  color: ${theme.colors.black};
  border-radius: ${theme.radius.base};
  :hover {
    background-color: ${theme.colors.grayLighter};
  }
`

const MenuLinkAlt = styled(MenuLink)`
  background-color: ${theme.colors.orange};
  color: ${theme.colors.white};
  justify-content: center;

  :hover {
    background-color: ${theme.colors.orange};
  }
`

const StyledWalletIcon = styled(WalletIcon)`
  margin-right: 12px;
`

const StyledErrorIcon = styled(ErrorIcon)`
  color: ${theme.colors.red};
  margin-right: 4px;
`

const MenuLinkContent = styled(Flex)`
  flex: 1 0 auto;
`

function MenuWalletSkeleton(): JSX.Element {
  return (
    <MenuLink data-testid="menu-wallet-skeleton">
      <Skeleton height="19px" />
    </MenuLink>
  )
}

function MenuWalletWrapper({
  href,
  isError,
  walletData,
}: {
  href: string
  isError: boolean
  walletData: IData
}): JSX.Element {
  const renderActiveWallet = useCallback(
    () => (
      <Typography
        data-testid="wallet-balance"
        type="bsubtitle"
        color="orange"
      >
        {CurrencyFormatter(walletData.balance)}
      </Typography>
    ),
    [walletData.balance],
  )

  const renderElse = useCallback(() => {
    const message =
      walletData.status === 'suspended'
        ? 'Wallet Ditangguhkan'
        : 'Wallet Diblokir'
    return (
      <Flex
        data-testid="wallet-not-active-wrapper"
        alignItems="center"
      >
        <StyledErrorIcon />
        <Typography
          type="rcaption"
          color="red"
          data-testid="wallet-error-message"
        >
          {isError ||
          walletData.message ===
            'user not in fitur flag or something wrong'
            ? 'Dalam Perbaikan'
            : message}
        </Typography>
      </Flex>
    )
  }, [isError, walletData.message, walletData.status])

  return (
    <MenuLink data-testid="menu-wallet-wrapper" href={href}>
      <StyledWalletIcon fill={theme.colors.grayDark} />
      <MenuLinkContent justifyContent="space-between">
        <>
          <span>Wallet</span>
          {walletData.status === 'active'
            ? renderActiveWallet()
            : renderElse()}
        </>
      </MenuLinkContent>
    </MenuLink>
  )
}

function MenuWalletNotFound(): JSX.Element {
  return (
    <MenuLinkAlt
      data-testid="menu-wallet-not-found"
      href={`${WEB_DESKTOP_RALALI_URL}/wallet/activation`}
    >
      <StyledWalletIcon fill={theme.colors.white} />
      <Typography fontWeight="bold" color={theme.colors.white}>
        Aktifkan Dompet
      </Typography>
    </MenuLinkAlt>
  )
}

function MenuWallet(): JSX.Element | null {
  const {
    data,
    isError,
    isLoading,
    alreadyGetWallet,
  } = useStoreState((state) => state.wallet)

  const { getWalletBalance, setWalletMaintenance } = useStoreActions(
    (actions) => actions.wallet,
  )

  const isFeatureFlagLoaded = useFeatureFlagLoaded()
  const isWalletMaintenance = useFeatureFlags(
    'biz--wallet--enable-maintenance--global',
  )

  useEffect(() => {
    if (isFeatureFlagLoaded) {
      if (!alreadyGetWallet && !isWalletMaintenance) {
        getWalletBalance({ currencyCode: 'IDR' })
      } else if (isWalletMaintenance) {
        setWalletMaintenance()
      }
    }
  }, [alreadyGetWallet, isFeatureFlagLoaded, isWalletMaintenance])

  const isWalletNotFound = useMemo(
    () =>
      data.name === 'DATA_NOT_FOUND' || data.status === 'unverified',
    [data.name, data.status],
  )

  if (isLoading || !isFeatureFlagLoaded) {
    return <MenuWalletSkeleton />
  }

  if (isWalletNotFound) {
    return <MenuWalletNotFound />
  }

  return (
    <MenuWalletWrapper
      href={`${WEB_DESKTOP_RALALI_URL}/wallet`}
      walletData={data}
      isError={isError || isWalletMaintenance}
    />
  )
}

export default MenuWallet
